import * as CookieConsent from 'vanilla-cookieconsent';
import 'vanilla-cookieconsent/dist/cookieconsent.css';

import './consent.css';
import { DEFAULT_SITE_URL, isPublicSite } from '@/marketing/seo/site-url';

import { CMS_COOKIE_BANNER_REVISION, CMS_CONSENT_COPY } from './_generated/cms-consent';
import {
  CONSENT_COPY,
  CONSENT_FALLBACK_LANG,
  COPY_BEFORE_POLICY,
  type ConsentCopy,
} from './translations';

/**
 * Cookie consent (DIT-402).
 *
 * Imperative on purpose: the library owns its own DOM outside the React tree, so
 * there is nothing for the prerender to bake and nothing to hydrate. That matters
 * here — every marketing route is prerendered by `vite-react-ssg`, and a React
 * banner would either ship a frozen "not yet asked" state into the static HTML or
 * mismatch on hydration. It also means one init covers both the marketing site
 * and the funnel, which are separate route trees with separate i18n systems.
 *
 * Since DIT-513 the analytics and marketing categories govern what the Google Tag
 * Manager container measures — GA4, and whatever advertising tags it carries — and
 * the attribution kept for a signup. The container itself loads for every visitor:
 * Consent Mode reads the answer (`@shared/analytics`) in its advanced mode, so
 * Google's tags send pings without cookies until a category is granted, and nothing
 * for measurement or advertising sets a cookie or is stored before that.
 *
 * Every category but the necessary one is opt-in: nothing for it sets a cookie or
 * stores anything until it is granted, and withdrawing it removes what it set
 * (see `CATEGORIES`).
 */

export const CONSENT_CATEGORIES = ['necessary', 'analytics', 'marketing', 'preferences'] as const;
export type ConsentCategory = (typeof CONSENT_CATEGORIES)[number];

/**
 * The newest banner this code can show. Bump to re-ask everyone — e.g. when a
 * category is added or its purpose changes.
 *
 * 2 (DIT-517): Analytics and Marketing gained their services and cookie tables.
 * A consent stored under 1 was given against two empty sections, and it lasts six
 * months, so without the bump it would count as consent to services the visitor
 * was never shown.
 *
 * Still 2 with DIT-513's rows for `di_attr_v2` and `di_evt_*`, on one condition:
 * nobody holds a revision-2 consent given without those rows. Revision 2 is shown only
 * once the reviewed privacy policy is published (`bannerRevision`), so that holds as
 * long as this landing is live before the policy is published. Released after it,
 * this has to be 3.
 */
export const LATEST_REVISION = 2;

/**
 * Which banner this build shows: the one the **published** privacy policy goes with.
 *
 * The banner and the policy describe the same cookies, so they change on the same
 * day. The policy says which banner it goes with (`cookie_banner_revision`, set on the
 * version in the CMS), the build reads it from the CMS with the policy itself, and
 * publishing a policy rebuilds the site — so publishing the reviewed policy in the CMS
 * admin is what switches the banner, and nothing ships it separately (DIT-517).
 *
 * A build with no CMS behind it shows 1, the banner that goes with the policy that was
 * live when this was written; a number newer than this code knows shows the newest it
 * does.
 */
export function bannerRevision(published: number = CMS_COOKIE_BANNER_REVISION): number {
  const n = Math.trunc(Number(published)) || 1;
  return Math.min(Math.max(n, 1), LATEST_REVISION);
}

export const REVISION = bannerRevision();

/**
 * The name the visitor's answer is stored under: `di_consent` on the public site,
 * `di_consent_stage` on every other copy of it.
 *
 * The library writes its cookie with a Domain attribute for the page's host, so
 * the public site's cookie on done-it.app is sent to every subdomain, stage
 * included. With one name for both, a browser that had visited the public site
 * held two cookies on stage, and the library reads the first — the public site's,
 * the older one. While the two asked under different revisions, stage showed the
 * banner again on every load however often it was answered (DIT-517 QA). A cookie
 * belonging to the public site cannot be replaced from stage without erasing the
 * public site's answer, so stage keeps its own. Decided by the same site URL that
 * decides whether the site is indexed, so the two cannot disagree — which also
 * means a copy on a done-it.app subdomain must keep `VITE_SITE_URL` unset, as the
 * pipelines already require for stage, or it stores its answer as `di_consent`
 * again.
 */
export function consentCookieName(
  siteUrl: string = import.meta.env.VITE_SITE_URL || DEFAULT_SITE_URL,
): string {
  return isPublicSite(siteUrl) ? 'di_consent' : 'di_consent_stage';
}

export const CONSENT_COOKIE_NAME = consentCookieName();

/**
 * The cookie string that erases the `di_consent` a non-public copy wrote for itself
 * before it had a name of its own, or `null` where there is nothing of ours to erase.
 *
 * Nothing reads that cookie any more, and left alone it would ride every request
 * for the rest of its six months without being listed in the banner. Erased for
 * this host only, the Domain the library wrote it with — the library adds a Domain
 * for any host with a dot in it — and never for a parent: the public site's cookie
 * is set for done-it.app, a different Domain, and must survive. On the public site
 * `di_consent` is the answer itself, so nothing is erased there.
 *
 * Written on every start-up of such a copy, which costs one cookie write; it can go
 * once the last of those cookies has expired, six months after this reaches stage.
 */
export function legacyConsentEraser(cookieName: string, host: string): string | null {
  if (cookieName === 'di_consent') return null;
  const domain = host.includes('.') ? `; domain=${host}` : '';
  return `di_consent=; path=/${domain}; expires=Thu, 01 Jan 1970 00:00:01 GMT`;
}

/**
 * What withdrawing each category takes with it.
 *
 * The library clears only what a category declares, so without this a withdrawal
 * left every cookie in place and the tags on the open page kept running — Consent
 * Mode reaches Google's tags, not Meta's or Clarity's. The reload is what stops
 * those. Patterns rather than names, because `_ga_<stream id>` and the four
 * `_gcl_*` variants are not known in advance.
 *
 * Only cookies on our own domain can go. `IDE`, `test_cookie` and `CLID` live on
 * doubleclick.net and clarity.ms, where a page on done-it.app cannot reach them;
 * the privacy policy says so rather than promising it.
 *
 * The library erases on the page's host and, for a `www.` host, on the parent
 * domain too, which is where GA and the pixels put their cookies. On a host with
 * any other subdomain (stage) the parent-domain copy survives.
 */
export const CATEGORIES = {
  necessary: { enabled: true, readOnly: true },
  analytics: {
    autoClear: {
      cookies: [{ name: /^_ga(_.+)?$/ }, { name: /^_cl(ck|sk)$/ }],
      reloadPage: true,
    },
  },
  marketing: {
    autoClear: {
      cookies: [{ name: /^_gcl_/ }, { name: /^_fb[pc]$/ }],
      reloadPage: true,
    },
  },
  preferences: {
    autoClear: {
      cookies: [{ name: 'cw_conversation' }],
      reloadPage: true,
    },
  },
} satisfies Record<ConsentCategory, CookieConsent.Category>;

/**
 * Six months, the retention the Belgian DPA and CNIL both point at. Longer is
 * legal in places but this is a compliance product; the conservative number is
 * the one we can defend.
 */
const EXPIRES_AFTER_DAYS = 182;

/** Builds the library's nested translation shape from one flat copy entry. */
function translation(c: ConsentCopy, revision: number) {
  const tables = revision >= 2;
  return {
    consentModal: {
      title: c.title,
      description: c.description,
      acceptAllBtn: c.acceptAll,
      acceptNecessaryBtn: c.rejectAll,
      showPreferencesBtn: c.manage,
      // No close icon on the banner on purpose. The library renders one as soon
      // as `closeIconLabel` is set, and a bare X next to Accept/Reject is an
      // ambiguous third option — the visitor has to make an actual choice.
    },
    preferencesModal: {
      title: c.prefTitle,
      acceptAllBtn: c.acceptAll,
      acceptNecessaryBtn: c.rejectAll,
      savePreferencesBtn: c.save,
      closeIconLabel: c.close,
      sections: [
        { title: c.introTitle, description: c.introBody },
        {
          title: c.necessaryTitle,
          description: c.necessaryBody,
          linkedCategory: 'necessary',
          // The table lists local storage alongside the cookies, which is why the first
          // column is "Name" and not "Cookie": ePrivacy art. 5(3) covers both
          // identically, and the description above enumerates what is kept, so
          // the table and that sentence have to agree — see `necessaryBody`.
          cookieTable: {
            headers: { name: c.colName, purpose: c.colPurpose, expiry: c.colExpiry },
            body: [
              { name: 'pref_locale', purpose: c.localeCookie, expiry: c.expiry12m },
              { name: CONSENT_COOKIE_NAME, purpose: c.consentCookie, expiry: c.expiry6m },
              {
                name: 'doneit-subscription-v1',
                purpose: c.signupState,
                expiry: c.expiryStorage,
              },
            ],
          },
        },
        {
          title: c.analyticsTitle,
          description: c.analyticsBody,
          linkedCategory: 'analytics',
          // DIT-517. These two categories carried a description and no table
          // while the other two named every cookie. This table is now the only
          // inventory: the privacy policy names the services and points here for
          // the cookies rather than repeating them (ADR-0035 decision 5, amended).
          //
          // Nothing in this category sets a cookie until it is accepted, so the
          // names are listed ahead of anything setting them. That is the point
          // rather than a mistake: a visitor is being asked to accept them.
          //
          // ⚠ Four names are still out, and the reason is worth keeping.
          // Clarity's Microsoft Advertising integration is **off** — verified in
          // the project settings — so `MUID` does not arrive that way. But
          // Clarity syncs with clarity.ms on its own, and Microsoft documents
          // `MUID`, `ANONCHK`, `MR` and `SM` as cookies that sync can set. The
          // other rows follow from how the tags are configured, conditional ones
          // like `_fbc` included; whether these four appear is Clarity's own
          // behaviour, which no setting of ours decides. They go in when Preview
          // shows them on this site.
          cookieTable: tables
            ? {
                headers: { name: c.colName, purpose: c.colPurpose, expiry: c.colExpiry },
                body: [
                  { name: '_ga', purpose: c.gaVisitor, expiry: c.expiry24m },
                  { name: '_ga_81WJ2Q6M2X', purpose: c.gaSession, expiry: c.expiry24m },
                  { name: '_clck', purpose: c.clarityVisitor, expiry: c.expiry12m },
                  { name: '_clsk', purpose: c.claritySession, expiry: c.expiry1d },
                  { name: 'CLID (clarity.ms)', purpose: c.clarityId, expiry: c.expiry12m },
                  // DIT-513's two local-storage entries. Either category writes them —
                  // the campaign under Analytics, the advertisement clicked under
                  // Advertising, the markers under whichever measurement is allowed — so
                  // both tables name both. They are removed on withdrawal by the code
                  // that writes them, not by `autoClear`, which only reaches cookies.
                  {
                    name: 'di_attr_v2',
                    purpose: c.attributionCampaign,
                    expiry: c.expiryStorage90d,
                  },
                  { name: 'di_evt_*', purpose: c.eventMarkers, expiry: c.expiryStorage90d },
                ],
              }
            : undefined,
        },
        {
          title: c.marketingTitle,
          description: c.marketingBody,
          linkedCategory: 'marketing',
          // Remarketing is **on** — two lists exist in the Ads account — which
          // is why `IDE` and `test_cookie` are here. That was not visible in the
          // container at all: the Google tag carries no remarketing settings of
          // its own, it is decided in the account, so this pair would have been
          // missing from the banner until somebody found them in a browser.
          //
          // The four click cookies share a row because they share a purpose and
          // a lifetime; which of them appears depends on how the campaign is
          // built, and a reader is owed the names rather than the mechanics.
          cookieTable: tables
            ? {
                headers: { name: c.colName, purpose: c.colPurpose, expiry: c.colExpiry },
                body: [
                  { name: '_gcl_au', purpose: c.adsClick, expiry: c.expiry90d },
                  {
                    name: '_gcl_aw, _gcl_dc, _gcl_gs, _gcl_gb',
                    purpose: c.adsFromAd,
                    expiry: c.expiry90d,
                  },
                  { name: 'IDE (doubleclick.net)', purpose: c.remarketingId, expiry: c.expiry13m },
                  {
                    name: 'test_cookie (doubleclick.net)',
                    purpose: c.remarketingProbe,
                    expiry: c.expiry15min,
                  },
                  { name: '_fbp', purpose: c.metaBrowser, expiry: c.expiry90d },
                  { name: '_fbc', purpose: c.metaClick, expiry: c.expiry90d },
                  { name: 'di_attr_v2', purpose: c.attributionClick, expiry: c.expiryStorage90d },
                  { name: 'di_evt_*', purpose: c.eventMarkers, expiry: c.expiryStorage90d },
                ],
              }
            : undefined,
        },
        {
          title: c.preferencesTitle,
          description: c.preferencesBody,
          linkedCategory: 'preferences',
          cookieTable: {
            headers: { name: c.colName, purpose: c.colPurpose, expiry: c.colExpiry },
            body: [
              // DIT-336. Declared here rather than under "strictly necessary", where it
              // first landed only because the widget used to load before this banner was
              // answered — which left the visitor no way to refuse it. The chat now waits
              // for this category, so the declaration and the behaviour agree.
              { name: 'cw_conversation', purpose: c.supportChat, expiry: c.expiry12m },
            ],
          },
        },
      ],
    },
  };
}

const escapeHtml = (text: string) =>
  text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/**
 * The copy one locale's banner shows.
 *
 * Revision 1 is the banner that was live before DIT-517 (`COPY_BEFORE_POLICY`).
 * From revision 2 the CMS can override any string: a `consent.<key>` content block
 * (shared across sites) replaces that key in that language, so the wording the
 * lawyers settle on, and its translations, are edited in the admin rather than
 * shipped (DIT-517). The overrides are dormant until the policy they go with is
 * published. A block is plain text like every content block — escaped here, because
 * the library renders these strings as HTML — and an empty one keeps the text in
 * code, as everywhere else on the site.
 */
export function copyFor(code: string, revision: number): ConsentCopy {
  const base = CONSENT_COPY[code] ?? CONSENT_COPY[CONSENT_FALLBACK_LANG];
  if (revision < 2) return { ...base, ...(COPY_BEFORE_POLICY[code] ?? {}) };
  const overrides: Partial<ConsentCopy> = {};
  for (const [key, value] of Object.entries(CMS_CONSENT_COPY[code] ?? {})) {
    if (key in base && typeof value === 'string' && value.trim()) {
      overrides[key as keyof ConsentCopy] = escapeHtml(value);
    }
  }
  return { ...base, ...overrides };
}

/** Every locale's banner as the library takes it, for one revision. */
export function translationsFor(revision: number) {
  return Object.fromEntries(
    Object.keys(CONSENT_COPY).map((code) => [code, translation(copyFor(code, revision), revision)]),
  );
}

export const TRANSLATIONS = translationsFor(REVISION);

/**
 * The locale is the first path segment, the same source of truth the rest of the
 * site uses. Read from the URL rather than from either i18n instance because this
 * runs before both of them.
 */
function langFromPath(): string {
  if (typeof window === 'undefined') return CONSENT_FALLBACK_LANG;
  const first = window.location.pathname.split('/').filter(Boolean)[0]?.toLowerCase();
  return first && first in CONSENT_COPY ? first : CONSENT_FALLBACK_LANG;
}

/** Resolves once the banner is up. Null until `initConsent()` is called. */
let ready: Promise<unknown> | null = null;

/**
 * Who wants to know when the answer changes (DIT-513).
 *
 * The measurement layer has to hear about every change, not just the first one:
 * Consent Mode sends an *update* for each, and a visitor who accepts after
 * declining — or reopens the preferences and turns marketing off — must be
 * measured as they now are. The banner is the only thing that knows, so the
 * subscription lives here rather than the analytics module reaching into it.
 */
const listeners = new Set<() => void>();

export function onConsentChange(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function announce(): void {
  // One listener throwing must not stop the others, and must not surface as an
  // unhandled error inside the consent library's own callback.
  listeners.forEach((listener) => {
    try {
      listener();
    } catch {
      /* a subscriber's problem, not the banner's */
    }
  });
}

/** Boots the banner. Safe to call more than once; only the first call runs. */
export function initConsent(): void {
  if (ready || typeof window === 'undefined') return;

  const eraser = legacyConsentEraser(CONSENT_COOKIE_NAME, window.location.hostname);
  if (eraser) document.cookie = eraser;
  ready = CookieConsent.run({
    autoShow: true,
    // Opt-in: nothing for a non-necessary category sets a cookie or stores anything
    // until it is granted. Google's tags do run before that, in Consent Mode's
    // advanced mode, without cookies (`@shared/analytics`).
    mode: 'opt-in',
    revision: REVISION,
    cookie: {
      name: CONSENT_COOKIE_NAME,
      expiresAfterDays: EXPIRES_AFTER_DAYS,
      sameSite: 'Lax',
      path: '/',
    },
    guiOptions: {
      // Bottom CENTRE, not bottom-left: the marketing shell already owns that
      // corner with the language-suggestion nudge (`bottom-4 left-4`), and both
      // appear on a first visit whenever the browser language differs from the
      // URL locale — which is most of our audience. Bottom-left buried it.
      consentModal: { layout: 'box wide', position: 'bottom center', equalWeightButtons: true },
      preferencesModal: { layout: 'box', equalWeightButtons: true },
    },
    categories: CATEGORIES,
    language: {
      default: langFromPath(),
      // The path prefix decides the language, so no sniffing of the browser.
      translations: TRANSLATIONS,
    },
    // Both, not just `onChange`: `onConsent` fires on the first answer AND on
    // every later page load for a visitor whose choice is already stored, which
    // is the state Consent Mode has to be told about before its `wait_for_update`
    // window closes. `onChange` covers a visitor editing their answer.
    onConsent: announce,
    onChange: announce,
    // A banner that fails to start must not take the page down with it, and an
    // unhandled rejection here would surface as a console error on every load.
  }).catch(() => {});
}

/**
 * Keeps the banner on the locale the visitor is actually looking at. Language is
 * fixed at init, but the switcher navigates client-side, so nothing would reload.
 */
export function setConsentLanguage(code: string): void {
  const next = code?.toLowerCase();
  if (!ready || !next || !(next in CONSENT_COPY)) return;
  // Queue behind init rather than firing straight away: `run()` is async, so a
  // layout effect can reach this before the banner exists and the call would be
  // dropped on the floor.
  void ready.then(() => CookieConsent.setLanguage(next)).catch(() => {});
}

/**
 * Whether a category has been granted. Use this before storing or loading anything
 * non-essential — the tag manager container is the deliberate exception, loaded
 * for everyone because Consent Mode gates Google's tags inside it; for plain script
 * tags prefer the library's own `data-category` attribute so the tag can never
 * fire ahead of the check.
 */
export function hasConsent(category: ConsentCategory): boolean {
  try {
    return CookieConsent.acceptedCategory(category);
  } catch {
    // Called before init, or init failed. Deny — the fail-safe answer for
    // consent is always "no".
    return false;
  }
}
